import React from "react";
import { useTrainerLectureConfirmations, useSendTrainerLectureConfirmationRequest } from "api/hooks";
import {
  TrainerLectureConfirmationApi,
  TRAINER_LECTURE_CONFIRMATION_STATUS,
} from "api/trainers/trainerLectureConfirmationsApi";
import { TanTable, TanTableColumn, usePagination } from "components/molecules";
import { PaziaButton } from "components/atoms";
import { formatDate, useToaster } from "utils";

const STATUS_LABEL: Record<string, string> = {
  [TRAINER_LECTURE_CONFIRMATION_STATUS.NOT_REQUESTED]: "Niepotwierdzone",
  [TRAINER_LECTURE_CONFIRMATION_STATUS.PENDING]: "Oczekuje",
  [TRAINER_LECTURE_CONFIRMATION_STATUS.CONFIRMED]: "Potwierdzone",
};

function getLectureStart(day: Date, timeFrom: string): Date {
  const [hours, minutes] = timeFrom.split(":").map(Number);
  const d = new Date(day);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes);
}

function hasLectureStarted(day: Date, timeFrom: string): boolean {
  return new Date() >= getLectureStart(day, timeFrom);
}

const getTodayIso = (): string => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
};

interface TrainerLectureConfirmationsTableProps {
  trainerId?: number;
  courseId?: number;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
}

export const TrainerLectureConfirmationsTable = ({
  trainerId,
  courseId,
  status,
  dateFrom,
  dateTo,
}: TrainerLectureConfirmationsTableProps) => {
  const { showSuccess, showError } = useToaster();
  const { limit, onPaginationChange, skip, pagination } = usePagination();

  const effectiveDateFrom = dateFrom ?? getTodayIso();

  const { confirmations, total, isLoading, isFetching, refetch } = useTrainerLectureConfirmations(
    skip,
    limit,
    trainerId,
    courseId,
    effectiveDateFrom,
    dateTo,
    status,
  );
  const sendRequestMutation = useSendTrainerLectureConfirmationRequest();

  const onSendRequest = async (row: TrainerLectureConfirmationApi) => {
    try {
      await sendRequestMutation.mutateAsync({ trainerId: row.trainerId, courseScheduleId: row.courseScheduleId });
      showSuccess("Wysłano prośbę o potwierdzenie");
      refetch();
    } catch {
      showError("Nie udało się wysłać prośby o potwierdzenie");
    }
  };

  const columns = React.useMemo<TanTableColumn<TrainerLectureConfirmationApi>[]>(() => {
    const cols: TanTableColumn<TrainerLectureConfirmationApi>[] = [];
    if (!trainerId) {
      cols.push({
        id: "trainer",
        header: "Trener",
        enableColumnFilter: false,
        size: 200,
        cell: (info) => `${info.row.original.trainerFirstName} ${info.row.original.trainerSurname}`,
      });
    }
    if (!courseId) {
      cols.push({
        id: "courseName",
        accessorKey: "courseName",
        header: "Kurs",
        enableColumnFilter: false,
        size: 250,
      });
    }
    cols.push(
      {
        id: "day",
        accessorKey: "day",
        header: "Data zajęć",
        enableColumnFilter: false,
        size: 130,
        cell: (info) => formatDate(info.getValue<Date>(), false),
      },
      {
        id: "time",
        hideOnMobile: true,
        header: "Godzina",
        enableColumnFilter: false,
        size: 100,
        cell: (info) => `${info.row.original.timeFrom}–${info.row.original.timeTo}`,
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        enableColumnFilter: false,
        size: 130,
        cell: (info) => STATUS_LABEL[info.getValue<string>()],
      },
      {
        id: "confirmedAt",
        hideOnMobile: true,
        accessorKey: "confirmedAt",
        header: "Potwierdzono kiedy",
        enableColumnFilter: false,
        size: 170,
        cell: (info) => {
          const value = info.getValue<Date | null>();
          return value ? formatDate(value, true) : "—";
        },
      },
      {
        id: "actions",
        header: "Akcje",
        enableColumnFilter: false,
        size: 200,
        cell: (info) => {
          const row = info.row.original;
          if (row.status === TRAINER_LECTURE_CONFIRMATION_STATUS.CONFIRMED) return null;
          if (hasLectureStarted(row.day, row.timeFrom)) return null;
          return (
            <PaziaButton
              type="tertiary"
              title="Poproś o potwierdzenie"
              onClick={() => onSendRequest(row)}
              disabled={sendRequestMutation.isPending}
            />
          );
        },
      },
    );
    return cols;
  }, [trainerId, courseId, sendRequestMutation.isPending]);

  return (
    <TanTable
      data={confirmations}
      columns={columns}
      total={total}
      loading={isLoading}
      fetching={isFetching}
      onPaginationChange={onPaginationChange}
      pagination={pagination}
    />
  );
};
